import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ColDef,
  ColumnState,
  DefaultMenuItem,
  FilterChangedEvent,
  GetContextMenuItemsParams,
  GridApi,
  GridReadyEvent,
  IRowNode,
  MenuItemDef,
  SortChangedEvent,
} from 'ag-grid-community'
import { useGridContext } from '../../../containers/BulkSheetView/hooks/useGridContext'
import { PageArea, PageProps } from '../..'
import {
  isAmountColumn,
  profitLossItemsTreeGridOptions,
  refreshDynamicColumnDef,
  refreshPinnedRow,
} from '../gridOptions'
import { useProfitLossItemData } from '../hooks/profitLossItemsData'
import { StoredPageState, usePageState } from '../hooks/pageState'
import {
  AccountCategory,
  BudgetResultType,
  BudgetResultTypeType,
  ProfitLossItemRow,
  RowFilterKey,
  RowFilterKeyType,
  RowStyle,
  RowStyleType,
  createNewProfitLossItemRow,
} from '../profitLossItems'
import store from '../../../../store'
import {
  MessageLevel,
  addGlobalMessage,
  addScreenMessage,
} from '../../../../store/messages'
import { intl } from '../../../../i18n'
import { getRowNumber } from '../../../containers/BulkSheetView/lib/gridApi'
import { doNotRequireSave } from '../../../../store/requiredSaveData'
import { useExportExcel, useExportExcelSimple } from '../hooks/excel'
import '../../../../styles/agGrid.scss'
import { useKeyBind } from '../../../hooks/useKeyBind'
import { KEY_SAVE } from '../../../model/keyBind'
import _ from 'lodash'
import { useBulkSheetState } from '../hooks/bulkSheetState'
import { ROW_HEIGHT } from '../../../containers/BulkSheet'
import SavedUIStateDialog from '../../../components/dialogs/SavedUIStateDialog'
import { UiStateKey } from '../../../../lib/commons/uiStates'
import { SavedUIState } from '../../../components/dialogs/SavedUIStateDialog/SavedUIStateList'
import {
  InputError,
  addInputError,
} from '../../../containers/BulkSheetView/lib/validation'
import validator from '../../../containers/meta/validator'
import BoolExpression from '../../../../utils/boolExpression'
import { FunctionProperty } from '../../../../lib/commons/appFunction'
import { DateTerm } from '../../../../utils/date'
import { useMasterData } from '../hooks/masterData'
import {
  AddSize,
  addMultipleRows,
  addRow,
  deleteRow,
  setAllBudgetAmountToResult,
  setSingleBudgetAmountToResult,
} from '../gridOptions/contextMenu'
import { FinancialStatementAccountsType } from '../../LedgerAccounts/ledgerAccounts'
import AddRowCountInputDialog from '../../../containers/BulkSheet/AddRowCountInputDialog'
import {
  collapseAllRows,
  expandAllRows,
} from '../../../containers/BulkSheetView/gridOptions/contextMenu'
import { ProjectDetail } from '../../../../lib/functions/project'
import { dateVoService } from '../../../../domain/value-object/DateVO'
import { SortedColumnState } from '../../../model/bulkSheetColumnSortState'
import { useColumnSetting } from '../../../containers/BulkSheetView/components/columnSelector/useColumnSetting'
import ProfitLossItemHeader, {
  ColumnQuickFilterKey,
  RowFilterProps,
} from '../components/Header'
import ProfitLossItemsSimpleView from './ProfitLossItemsSimpleView'
import { profitLossItemsSimpleGridOptions } from '../gridOptions/profitLossItemsSimpleGridOptions'
import {
  addSimpleRow,
  setSimpleAllBudgetAmountToResult,
  setSimpleSingleBudgetAmountToResult,
} from '../gridOptions/simpleContextMenu'
import ProfitLossItemsTreeView from './ProfitLossItemsTreeView'

export const CancelConfirmState = {
  OpenByReload: 'OPEN_BY_RELOAD',
  OpenByAccountCategory: 'OPEN_BY_ACCOUNT_CATEGORY',
  OpenByRowStyle: 'OPEN_BY_ROW_STYLE',
  Close: 'CLOSE',
} as const

export type CancelConfirmStateType =
  (typeof CancelConfirmState)[keyof typeof CancelConfirmState]

interface OwnProps {
  currentProject: ProjectDetail
}

type Props = OwnProps & PageProps

let localRowFilterTypes: BudgetResultTypeType[] = []

const ProfitLossItemsViewBase = (props: Props) => {
  const [loading, setLoading] = useState(false)
  const [cancelConfirmState, setCancelConfirmState] =
    useState<CancelConfirmStateType>(CancelConfirmState.Close)
  const [openUiState, setOpenUiState] = useState(false)
  const [filteredColumns, setFilteredColumns] = useState<ColDef[]>([])
  const [sortColumnsState, setSortColumnsState] = useState<SortedColumnState[]>(
    []
  )
  const [openAddMultipleRows, setOpenAddMultipleRows] = useState(false)
  const [paramsForAddMultiple, setParamsForAddMultiple] = useState<any>()

  const { data, fetchRecords, setData, save, summaryRows } =
    useProfitLossItemData()
  const { fetchMasterRecords, masterTree } = useMasterData()

  const columnSetting = useColumnSetting()
  const pageState = usePageState(props.uuid)
  const prevPageState = useRef<StoredPageState>({
    startDate: '',
    endDate: '',
    accountCategory: undefined,
    rowHeight: ROW_HEIGHT.SMALL,
    rowStyle: RowStyle.Tree,
  })

  const gridOptions = useMemo(() => {
    return profitLossItemsTreeGridOptions()
  }, [])

  const simpleGridOptions = useMemo(() => {
    return profitLossItemsSimpleGridOptions()
  }, [])

  const gridApiRef = useRef<GridApi>()
  const [isGridReady, setIsGridReady] = useState(false)
  const { contextRef, updateContext } = useGridContext(
    gridApiRef.current,
    gridOptions.context
  )

  const simpleGridApiRef = useRef<GridApi>()
  const [isSimpleGridReady, setIsSimpleGridReady] = useState(false)
  const { contextRef: simpleContextRef, updateContext: updateSimpleContext } =
    useGridContext(simpleGridApiRef.current, simpleGridOptions.context)

  const onExportExcel = useExportExcel(gridApiRef.current!)
  const onExportSimpleExcel = useExportExcelSimple(simpleGridApiRef.current!)
  const bulkSheetState = useBulkSheetState(
    gridApiRef.current,
    props.uuid,
    UiStateKey.ProfitLossItems
  )

  const onTreeGridReady = useCallback(
    (event: GridReadyEvent) => {
      gridApiRef.current = event.api
      setIsGridReady(true)
      bulkSheetState.onGridReady()
    },
    [bulkSheetState]
  )

  const onSimpleGridReady = useCallback(
    (event: GridReadyEvent) => {
      simpleGridApiRef.current = event.api
      setIsSimpleGridReady(true)
      bulkSheetState.onGridReady()
    },
    [bulkSheetState]
  )

  const refreshAll = useCallback(
    async (
      startDate: string,
      endDate: string,
      accountCategory: string | undefined
    ) => {
      if (!accountCategory) return
      try {
        setLoading(true)
        await fetchRecords(startDate, endDate, accountCategory)
        bulkSheetState.restoreExpandedRows()
        store.dispatch(doNotRequireSave())
      } finally {
        updateContext({
          draggableNodeId: undefined,
          errors: new InputError(),
          accountCategory,
        })
        updateSimpleContext({
          errors: new InputError(),
          accountCategory,
        })
        setLoading(false)
      }
    },
    [bulkSheetState, fetchRecords, updateContext, updateSimpleContext]
  )

  const onChangeDateTerm = useCallback(
    (term: DateTerm) => {
      if (!!term.startDate) {
        pageState.setStartDate(term.startDate)
      }
      if (!!term.endDate) {
        pageState.setEndDate(term.endDate)
      }
      const states = gridApiRef.current
        ?.getColumns()
        ?.filter(v => {
          if (v.isVisible()) return false
          const parent = v.getParent()
          if (!parent || !isAmountColumn(parent.getGroupId())) return false
          return dateVoService.isBetween(
            dateVoService.construct(v.getColId()),
            dateVoService.construct(term.startDate),
            dateVoService.construct(term.endDate),
            '[]'
          )
        })
        .map(v => ({
          colId: v.getColId(),
          hide: false,
        }))
      gridApiRef.current?.applyColumnState({ state: states })
    },
    [pageState]
  )

  const prevData = useRef<ProfitLossItemRow[]>([])
  prevData.current = data
  const hasChanged = useCallback((): boolean => {
    return (prevData.current || data).some(v => v.added || v.edited)
  }, [data])

  const execReload = useCallback(async () => {
    refreshAll(
      pageState.startDate,
      pageState.endDate,
      pageState.accountCategory
    )
  }, [
    pageState.accountCategory,
    pageState.endDate,
    pageState.startDate,
    refreshAll,
  ])

  const onReload = useCallback(() => {
    if (hasChanged()) {
      setCancelConfirmState(CancelConfirmState.OpenByReload)
    } else {
      execReload()
    }
  }, [execReload, hasChanged])

  const validateBeforeSubmit = useCallback(() => {
    const activeApi =
      pageState.rowStyle === RowStyle.Tree
        ? gridApiRef.current
        : simpleGridApiRef.current
    if (!activeApi || !contextRef.current?.errors) {
      return
    }
    const errors = new InputError()
    const dupeMsg = intl.formatMessage(
      { id: 'bulksheet.error.duplicate.unique.column.data' },
      {
        fieldName: intl.formatMessage({
          id: 'profitLossItems.duplicate.pattern',
        }),
      }
    )
    const colIds = activeApi.getColumnState().map(col => col.colId)
    const items = data.filter(v => !v.body.type)
    items.forEach(row => {
      const node = activeApi.getRowNode(row.uuid)
      if (!node) return
      colIds.forEach(colId => {
        const value = activeApi.getCellValue({ colKey: colId, rowNode: node })
        const colDef = activeApi.getColumn(colId)?.getColDef()
        if (!colDef || !colDef.cellRendererParams || !!colDef.field) {
          return
        }
        const uiMeta = {
          requiredIf: BoolExpression.of(true),
        }

        const error = validator
          .validate(value, row, uiMeta as FunctionProperty, () => undefined)
          ?.getMessage()
        if (error) {
          addInputError(errors, node.id, error, colDef)
        }
        if (colDef.field === 'body.name') {
          const isDupe = items.some(
            v =>
              (!!row.editedData || row.added) &&
              v.uuid !== row.uuid &&
              v.body?.financialState?.uuid === row.body?.financialState?.uuid &&
              v.body?.generalLedger?.uuid === row.body?.generalLedger?.uuid &&
              v.body?.subsidiary?.uuid === row.body?.subsidiary?.uuid &&
              !!v.body?.name &&
              !!row.body?.name &&
              v.body?.name === row.body?.name
          )
          if (isDupe) {
            addInputError(errors, node.id, dupeMsg, colDef)
          }
        }
      })
    })
    updateContext({
      errors,
    })
  }, [contextRef, data, pageState.rowStyle, updateContext])

  const onSubmit = useCallback(async () => {
    if (!pageState.accountCategory) return
    const activeApi =
      pageState.rowStyle === RowStyle.Tree
        ? gridApiRef.current
        : simpleGridApiRef.current
    setLoading(true)
    try {
      activeApi?.stopEditing()
      validateBeforeSubmit()
      if (contextRef.current.errors?.hasMessage()) {
        store.dispatch(
          addGlobalMessage({
            type: MessageLevel.WARN,
            title: intl.formatMessage({ id: 'global.warning.businessError' }),
            text: contextRef.current.errors.toMessage(id => {
              const node = activeApi?.getRowNode(id)
              return !!node ? getRowNumber(node).toString() : ''
            }),
          })
        )
        return
      }
      bulkSheetState.base.saveImmediately()
      const response = await save(
        props.currentProject.uuid,
        pageState.startDate,
        pageState.endDate,
        pageState.accountCategory
      )
      if (!response.hasError && !response.hasWarning) {
        store.dispatch(
          addScreenMessage(props.uuid, {
            type: MessageLevel.SUCCESS,
            title: intl.formatMessage({ id: 'registration.complete' }),
          })
        )
        await execReload()
      }
    } finally {
      setLoading(false)
    }
  }, [
    bulkSheetState.base,
    contextRef,
    execReload,
    pageState.accountCategory,
    pageState.endDate,
    pageState.rowStyle,
    pageState.startDate,
    props.currentProject,
    props.uuid,
    save,
    validateBeforeSubmit,
  ])

  const isConditionChanged = useCallback(() => {
    if (
      !prevPageState.current ||
      prevPageState.current.accountCategory !== pageState.accountCategory ||
      prevPageState.current.startDate !== pageState.startDate ||
      prevPageState.current.endDate !== pageState.endDate ||
      prevPageState.current.rowStyle !== pageState.rowStyle
    ) {
      prevPageState.current = {
        accountCategory: pageState.accountCategory,
        startDate: pageState.startDate,
        endDate: pageState.endDate,
        rowHeight: pageState.rowHeight,
        rowStyle: pageState.rowStyle,
      }
      return true
    }
    return false
  }, [
    pageState.accountCategory,
    pageState.endDate,
    pageState.rowHeight,
    pageState.startDate,
    pageState.rowStyle,
  ])

  const onFilterChanged = useCallback(
    (e: FilterChangedEvent) => {
      const filter = bulkSheetState.onFilterChanged(e)
      setFilteredColumns(filter)
    },
    [bulkSheetState]
  )

  const onSortChanged = useCallback(
    (e: SortChangedEvent) => {
      const sorted = bulkSheetState.onSortChanged(e)
      const columnState = gridApiRef.current?.getColumnState()
      const sortedState: { [colId: string]: ColumnState } = {}
      columnState &&
        columnState.forEach(state => {
          if (state.sort) {
            sortedState[state.colId] = state
          }
        })
      const sortedList: SortedColumnState[] = sorted
        .filter((col): col is ColDef<any> => col !== null)
        .map(col => {
          return {
            colId: col.colId,
            field: col.field,
            headerName: col.headerName,
            sort: col.colId ? sortedState[col.colId]?.sort : null,
          }
        })
      setSortColumnsState(sortedList)
    },
    [bulkSheetState]
  )

  const onFirstDataRendered = useCallback(
    e => {
      bulkSheetState.onFirstDataRendered(e)
      setLoading(false)
    },
    [bulkSheetState]
  )

  const onChangeColumnFilter = useCallback((value: ColumnQuickFilterKey) => {
    if (value === ColumnQuickFilterKey.INITIAL) {
      gridApiRef.current?.resetColumnState()
      gridApiRef.current?.setFilterModel(null)
      gridApiRef.current?.onFilterChanged()
    } else {
      setOpenUiState(true)
    }
  }, [])

  const onDeletedFilterColumn = useCallback((column: ColDef) => {
    if (!column || !gridApiRef.current) return
    const filterModel = gridApiRef.current.getFilterModel()
    delete filterModel[column.colId || column.field || '']
    gridApiRef.current.setFilterModel(filterModel)
  }, [])

  const resetFilters = useCallback(() => {
    if (!gridApiRef.current) return
    gridApiRef.current.setFilterModel(null)
  }, [])

  const onDeleteSortedColumn = useCallback((colId: string | ColDef<any>) => {
    gridApiRef.current?.applyColumnState({
      state: [{ colId: colId.toString(), sort: null }],
    })
  }, [])

  const onDeleteSortedAllColumns = useCallback(() => {
    gridApiRef.current?.applyColumnState({
      defaultState: { sort: null },
    })
  }, [])

  const onChangeSortColumnState = useCallback(
    (colId: string | ColDef<any>, sort: 'asc' | 'desc' | null) => {
      gridApiRef.current?.applyColumnState({
        state: [{ colId: colId.toString(), sort }],
      })
    },
    []
  )

  const closeUiStateDialog = useCallback(() => {
    setOpenUiState(false)
  }, [])

  const callbackSelectUiState = useCallback(
    (uiState: SavedUIState) => {
      const { columnState, filterState } = uiState.UIState
      if (columnState) {
        bulkSheetState.base.saveColumnState(columnState)
        gridApiRef.current?.applyColumnState({
          state: columnState,
          applyOrder: true,
        })
      }
      if (filterState) {
        bulkSheetState.base.saveFilterState(filterState)
        gridApiRef.current?.setFilterModel(filterState)
      }
      closeUiStateDialog()
    },
    [bulkSheetState.base, closeUiStateDialog]
  )

  const pendingAccountCategory = useRef<string | undefined>()
  const pendingRowStyle = useRef<RowStyleType | undefined>()
  const setAccountCategory = useCallback(
    (value: string | undefined) => {
      setData([])
      pageState.setAccountCategory(value)
      updateContext({
        accountCategory: value,
      })
    },
    [setData, pageState, updateContext]
  )

  const onChangeAccountCategory = useCallback(
    (value: string | undefined) => {
      if (hasChanged()) {
        pendingAccountCategory.current = value
        setCancelConfirmState(CancelConfirmState.OpenByAccountCategory)
      } else {
        pendingAccountCategory.current = undefined
        setAccountCategory(value)
      }
    },
    [hasChanged, setCancelConfirmState, setAccountCategory]
  )

  const onCancelConfirm = useCallback(
    (state: CancelConfirmStateType) => {
      if (CancelConfirmState.OpenByReload === state) {
        execReload()
      } else if (CancelConfirmState.OpenByAccountCategory === state) {
        setAccountCategory(pendingAccountCategory.current)
        pendingAccountCategory.current = undefined
      } else if (CancelConfirmState.OpenByRowStyle === state) {
        pageState.setRowStyle(pendingRowStyle.current)
        pendingRowStyle.current = undefined
      }
    },
    [execReload, pageState, setAccountCategory]
  )

  const contextMenu = useCallback(
    (params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
      const menuItems: (DefaultMenuItem | MenuItemDef)[] = [
        addRow(
          params,
          data,
          pageState.accountCategory,
          masterTree,
          createNewProfitLossItemRow,
          {
            setData,
          }
        ),
        addMultipleRows(params, pageState.accountCategory, () => {
          setOpenAddMultipleRows(true)
          setParamsForAddMultiple(params)
        }),
        deleteRow(params, data, !pageState.accountCategory, { setData }),
        'separator',
        setSingleBudgetAmountToResult(params),
        setAllBudgetAmountToResult(params),
        'separator',
        expandAllRows(params),
        collapseAllRows(params),
      ]
      return menuItems
    },
    [pageState.accountCategory, data, masterTree, setData]
  )

  const simpleContextMenu = useCallback(
    (params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
      const menuItems: (DefaultMenuItem | MenuItemDef)[] = [
        addSimpleRow(
          params,
          data,
          pageState.accountCategory,
          masterTree,
          createNewProfitLossItemRow,
          {
            setData,
          }
        ),
        addMultipleRows(params, pageState.accountCategory, () => {
          setOpenAddMultipleRows(true)
          setParamsForAddMultiple(params)
        }),
        deleteRow(params, data, !pageState.accountCategory, { setData }),
        'separator',
        setSimpleSingleBudgetAmountToResult(params),
        setSimpleAllBudgetAmountToResult(params),
      ]
      return menuItems
    },
    [data, masterTree, pageState.accountCategory, setData]
  )

  const closeAddMultipleRows = useCallback(() => {
    setOpenAddMultipleRows(false)
  }, [])

  const submitAddMultipleRows = useCallback(
    (count: number | undefined) => {
      try {
        if (!count) return
        const financialState =
          AccountCategory.OperatingRevenue === pageState.accountCategory
            ? masterTree.find(
                v =>
                  v.financialStatementAccountsType ===
                  FinancialStatementAccountsType.Sales
              )
            : undefined
        const generalLedger =
          !!financialState?.children && financialState?.children.length > 0
            ? financialState?.children[0]
            : undefined
        const result: ProfitLossItemRow[] = []
        for (let i = 0; i < count; i++) {
          const addItems = createNewProfitLossItemRow(
            financialState,
            generalLedger
          )
          result.push(...addItems)
        }
        const copyData = _.cloneDeep(data)
        const index = data.findIndex(
          v => v.uuid === paramsForAddMultiple.node?.data.uuid
        )
        if (pageState.rowStyle === RowStyle.Tree) {
          copyData.splice(index + AddSize, 0, ...result)
        } else if (pageState.rowStyle === RowStyle.Simple) {
          const spliceIndex = Math.floor(index / AddSize)
          copyData.splice((spliceIndex + 1) * AddSize, 0, ...result)
        }
        setData(copyData)
      } finally {
        closeAddMultipleRows()
      }
    },
    [
      pageState.accountCategory,
      pageState.rowStyle,
      masterTree,
      data,
      setData,
      paramsForAddMultiple,
      closeAddMultipleRows,
    ]
  )

  const updateLocalRowFilterTypes = useCallback(
    (types: RowFilterKeyType[] | undefined) => {
      localRowFilterTypes.splice(0)
      if (!types) return

      if (types.includes(RowFilterKey.Scheduled)) {
        localRowFilterTypes.push(BudgetResultType.Budget)
      }
      if (types.includes(RowFilterKey.Actual)) {
        localRowFilterTypes.push(BudgetResultType.Result)
      }
      if (types.includes(RowFilterKey.BudgetaryAmount)) {
        localRowFilterTypes.push(BudgetResultType.BudgetaryAmount)
      }
      if (types.includes(RowFilterKey.Forecast)) {
        localRowFilterTypes.push(BudgetResultType.Forecast)
      }
    },
    []
  )

  const onChangeRowFilter = useCallback(
    (values: RowFilterKeyType[]) => {
      if (pageState.setRowFilterTypes) {
        pageState.setRowFilterTypes(values)
      }
      updateLocalRowFilterTypes(values)
      if (pageState.rowStyle === RowStyle.Tree) {
        gridApiRef.current?.onFilterChanged()
      } else {
        simpleGridApiRef.current?.onFilterChanged()
        simpleGridApiRef.current?.redrawRows()
      }
    },
    [pageState, updateLocalRowFilterTypes]
  )

  const rowFilterProps: RowFilterProps[] = useMemo(() => {
    return [
      {
        enableKeys: [
          RowFilterKey.Forecast,
          RowFilterKey.BudgetaryAmount,
          RowFilterKey.Scheduled,
          RowFilterKey.Actual,
        ],
        values: pageState.rowFilterTypes || [],
        onChange: onChangeRowFilter,
      },
    ]
  }, [pageState.rowFilterTypes, onChangeRowFilter])

  const isExternalFilterPresent = useCallback(params => true, [])

  const rowFilterPass = useCallback((node: IRowNode<ProfitLossItemRow>) => {
    if (
      localRowFilterTypes.includes(BudgetResultType.Forecast) &&
      !node.data?.body.type
    ) {
      return true
    }
    return (
      !!node.data?.body.type &&
      localRowFilterTypes.includes(node.data.body.type as BudgetResultTypeType)
    )
  }, [])

  const onChangeRowStyle = useCallback(
    (value: string | undefined) => {
      if (hasChanged()) {
        pendingRowStyle.current = value as RowStyleType
        setCancelConfirmState(CancelConfirmState.OpenByRowStyle)
      } else {
        pendingRowStyle.current = undefined
        pageState.setRowStyle(value as RowStyleType)
      }
    },
    [hasChanged, pageState, setCancelConfirmState]
  )

  const onClickExport = useCallback(() => {
    if (pageState.rowStyle === RowStyle.Tree) {
      onExportExcel()
    } else if (pageState.rowStyle === RowStyle.Simple) {
      onExportSimpleExcel()
    }
  }, [onExportExcel, onExportSimpleExcel, pageState.rowStyle])

  useEffect(() => {
    const api =
      pageState.rowStyle === RowStyle.Tree
        ? gridApiRef.current
        : simpleGridApiRef.current

    if (!api || !pageState?.initialized) return

    refreshDynamicColumnDef({
      api,
      dateTerm: {
        startDate: pageState.startDate,
        endDate: pageState.endDate,
      },
    })
  }, [
    pageState.initialized,
    pageState.startDate,
    pageState.endDate,
    pageState.rowStyle,
    isGridReady,
    isSimpleGridReady,
  ])

  useEffect(() => {
    if (pageState.rowStyle === RowStyle.Tree) {
      gridApiRef.current?.resetRowHeights()
    } else if (pageState.rowStyle === RowStyle.Simple) {
      simpleGridApiRef.current?.resetRowHeights()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageState.rowHeight, pageState.rowStyle])

  useEffect(() => {
    if (pageState.initialized && isConditionChanged()) {
      refreshAll(
        pageState.startDate,
        pageState.endDate,
        pageState.accountCategory
      )
    }
  }, [
    isConditionChanged,
    pageState.accountCategory,
    pageState.initialized,
    pageState.startDate,
    pageState.endDate,
    pageState.rowStyle,
    refreshAll,
  ])

  useEffect(() => {
    fetchMasterRecords()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (masterTree && masterTree.length > 0) {
      updateContext({
        masters: masterTree,
      })
      updateSimpleContext({
        masters: masterTree,
      })
    }
  }, [updateContext, updateSimpleContext, masterTree])

  useEffect(() => {
    updateLocalRowFilterTypes(pageState.rowFilterTypes)
  }, [updateLocalRowFilterTypes, pageState])

  useEffect(() => {
    refreshPinnedRow(gridApiRef.current)
  }, [isGridReady, data])

  useEffect(() => {
    refreshPinnedRow(simpleGridApiRef.current)
  }, [isSimpleGridReady, data])

  useKeyBind(
    [
      {
        key: KEY_SAVE,
        fn: onSubmit,
        stopDefaultBehavior: true,
      },
    ],
    [data, save, pageState]
  )

  return (
    <PageArea>
      <ProfitLossItemHeader
        loading={loading}
        dateTerm={{
          startDate: pageState.startDate,
          endDate: pageState.endDate,
        }}
        onSearch={onChangeDateTerm}
        onSubmit={onSubmit}
        onReload={onReload}
        rowHeight={pageState.rowHeight}
        onChangeRowHeight={pageState.setRowHeight}
        onClickExport={onClickExport}
        accountCategory={pageState.accountCategory}
        onChangeAccountCategory={onChangeAccountCategory}
        rowFilters={rowFilterProps}
        filteredColumns={filteredColumns}
        onDeleteFilteredColumn={onDeletedFilterColumn}
        resetFilters={resetFilters}
        sortColumnState={sortColumnsState}
        onDeleteSortedColumn={onDeleteSortedColumn}
        onDeleteSortedAllColumns={onDeleteSortedAllColumns}
        onChangeSortColumnState={onChangeSortColumnState}
        columnSettingOpen={columnSetting.isOpen}
        onClickColumnSettingButton={columnSetting.toggle}
        rowStyle={pageState.rowStyle}
        onChangeRowStyle={onChangeRowStyle}
      />
      {pageState.rowStyle === RowStyle.Tree && (
        <ProfitLossItemsTreeView
          loading={loading}
          gridOptions={gridOptions}
          gridApi={gridApiRef.current}
          updateContext={updateContext}
          data={data}
          setData={setData}
          accountCategory={pageState.accountCategory}
          masterTree={masterTree}
          rowHeight={pageState.rowHeight}
          cancelConfirmState={cancelConfirmState}
          setCancelConfirmState={setCancelConfirmState}
          onCancelConfirm={onCancelConfirm}
          onColumnMoved={bulkSheetState.rememberColumnState}
          onColumnResized={bulkSheetState.rememberColumnState}
          onColumnVisible={bulkSheetState.rememberColumnState}
          onGridReady={onTreeGridReady}
          onRowGroupOpened={bulkSheetState.onRowGroupOpened}
          onFirstDataRendered={onFirstDataRendered}
          onFilterChanged={onFilterChanged}
          onSortChanged={onSortChanged}
          summaryRows={summaryRows}
          createRow={createNewProfitLossItemRow}
          getContextMenuItems={contextMenu}
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={rowFilterPass}
          applicationFunctionUuid={props.uuid}
          columnSetting={columnSetting}
          columnState={bulkSheetState.base.columnState}
          openSavedUiStateDialog={() => setOpenUiState(true)}
          initializeColumnState={() =>
            onChangeColumnFilter(ColumnQuickFilterKey.INITIAL)
          }
        />
      )}
      {pageState.rowStyle === RowStyle.Simple && (
        <ProfitLossItemsSimpleView
          loading={loading}
          gridOptions={simpleGridOptions}
          gridApi={simpleGridApiRef.current}
          data={data}
          accountCategory={pageState.accountCategory}
          masterTree={masterTree}
          rowHeight={pageState.rowHeight}
          cancelConfirmState={cancelConfirmState}
          setCancelConfirmState={setCancelConfirmState}
          onCancelConfirm={onCancelConfirm}
          onColumnMoved={bulkSheetState.rememberColumnState}
          onColumnResized={bulkSheetState.rememberColumnState}
          onColumnVisible={bulkSheetState.rememberColumnState}
          onGridReady={onSimpleGridReady}
          onRowGroupOpened={bulkSheetState.onRowGroupOpened}
          onFirstDataRendered={onFirstDataRendered}
          onFilterChanged={onFilterChanged}
          onSortChanged={onSortChanged}
          summaryRows={summaryRows}
          createRow={createNewProfitLossItemRow}
          getContextMenuItems={simpleContextMenu}
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={rowFilterPass}
          applicationFunctionUuid={props.uuid}
          columnSetting={columnSetting}
          columnState={bulkSheetState.base.columnState}
          openSavedUiStateDialog={() => setOpenUiState(true)}
          initializeColumnState={() =>
            onChangeColumnFilter(ColumnQuickFilterKey.INITIAL)
          }
        />
      )}
      <SavedUIStateDialog
        applicationFunctionUuid={props.uuid}
        open={openUiState}
        title={intl.formatMessage({
          id: 'savedUIState.BULK_SHEET_UI_STATE_COLUMN_AND_FILTER',
        })}
        uiStateKey={UiStateKey.BulkSheetUIStateColumnAndFilter}
        sharable={false}
        currentUIState={{
          columnState: bulkSheetState.base.columnState,
          filterState: bulkSheetState.base.filterState,
        }}
        onSelect={callbackSelectUiState}
        onClose={closeUiStateDialog}
      />
      <AddRowCountInputDialog
        open={openAddMultipleRows}
        submitHandler={submitAddMultipleRows}
        closeHandler={closeAddMultipleRows}
      />
    </PageArea>
  )
}

export default ProfitLossItemsViewBase
